import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap, catchError, mergeMap } from 'rxjs/operators';
import { Observable, EMPTY, zip, of, iif } from 'rxjs';
import { Action } from '@ngrx/store';

import {
  fetchDefaultPlaceAction,
  fetchDefaultAirportActionSuccess,
  selectDepartureTerminalAction,
  searchDefaultPlaceForIpActionSuccess
} from '@shared/base-search-bar/store/actions/search-bar.actions';
import { SearchDataService } from '@search-bar/services/search-data.service';
import { SearchIpService } from '@search-bar/services/search-ip.service';
import { SearchDataDefaultType } from '@search-bar/entities/search-data-default';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { SearchType } from '@search-bar/entities/search-type';
import { StorageServices } from '@shared/base-search-bar/services/storage.services';
import { isEmpty } from '@app/util/util';

@Injectable()
export class SearchDataEffects {

  constructor(
    private readonly action$: Actions,
    private readonly searchIp: SearchIpService,
    private readonly service: SearchDataService,
    private readonly storage: StorageServices
  ) { }

  fetchDefaultPlaceAction$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(fetchDefaultPlaceAction),
      switchMap(action => zip(
        this.searchIp.fetchIp(),
        this.storage.load()
      ).pipe(
        map(([result, storage]) => {
          const isBuses = action.searchType === SearchType.BUSES_RESULTS || action.searchType === SearchType.HOME_BUSES;
          const isFlights = action.searchType === SearchType.FLIGHT_RESULTS || action.searchType === SearchType.HOME_FLIGHT;
          const place = storage ? isBuses ? storage.departureTerminal : isFlights ? storage.departurePlace : {} : {};
          return { searchType: action.searchType, ip: result.ip, place, isBuses };
        }),
        catchError(() => EMPTY)
      )),
      switchMap(({ place, isBuses, searchType, ip }) => {
        const type = isBuses ? SearchDataDefaultType.BUS : SearchDataDefaultType.CITY;
        return zip(
          of(searchType),
          iif(
            () => isEmpty(place),
            this.service.fetchDefaultPlace(),
            of(null)
          ),
          iif(
            () => isEmpty(place),
            this.service.fetchDefaultPlaceFromIp(ip, type),
            of(null)
          )
        );
      }),
      map(([searchType, , defaultPlacesFromIp]) => ({ defaultPlacesFromIp, searchType })),
      mergeMap(({ defaultPlacesFromIp, searchType }) => {
        if (
          !isEmpty(defaultPlacesFromIp?.Marks) && this.validCodeIATA(defaultPlacesFromIp?.Marks?.[0]?.Code)
        ) {
          switch (searchType) {
            case SearchType.HOME_BUSES:
              return [selectDepartureTerminalAction({
                place: {
                  code: defaultPlacesFromIp.Marks[0].Code,
                  description: defaultPlacesFromIp.Marks[0].Name,
                  type: PlaceType.DEPARTURE,
                  country: defaultPlacesFromIp.Marks[0].CountryName
                } as SelectAutocompletePlace
              }),
              searchDefaultPlaceForIpActionSuccess()];
            default:
              if (defaultPlacesFromIp.Marks && defaultPlacesFromIp.Marks.length > 0 &&
                this.validCodeIATA(defaultPlacesFromIp.Marks[0].Code)) {
                return [fetchDefaultAirportActionSuccess({
                  place:
                    {
                      code: defaultPlacesFromIp.Marks[0].Code,
                      description: defaultPlacesFromIp.Marks[0].Name,
                      type: PlaceType.DEPARTURE,
                      country: defaultPlacesFromIp.Marks[0].CountryName
                    } as SelectAutocompletePlace
                }),
                searchDefaultPlaceForIpActionSuccess()];
              } else return [searchDefaultPlaceForIpActionSuccess()];
          }
        } else return [searchDefaultPlaceForIpActionSuccess()];
      })
    ));


  private validCodeIATA(code: string): boolean {
    return new RegExp('^[A-Za-z]{3}$').test(code);
  }
}

